import { Divider, Tab, TabList } from "@fluentui/react-components";
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import * as React from "react";
import { useCallback, useContext, useEffect, useState } from "react";
import { LayoutContext } from "../../../context/LayoutContext";
import { useNotificationWebSocket } from "../../../context/NotificationWebSocketContext";
import { useApiResource } from "../../../hooks/useApiResource";
import { getAirportConfig } from "../../../utils/common/CommonHelperFunctions";
import {
  AIRPORT_CODE_PARAMETER,
  EMPTY_STATE_MESSAGE,
} from "../../../utils/common/Constants";
import {
  buildDismissNotificationsPayload,
  filterAndSortNotificationsByStatus,
  formatFilteredNotifications,
  matchesSelectedTimeFilter,
} from "../../../utils/notification/NotificationPanelHelperFunctions";
import { NoDataCard } from "../../common/no-data-card/NoDataCard";
import { DEFAULT_FILTERS } from "../constants";
import {
  DismissNotificationsPayload,
  DismissSelectedNotificationsPayload,
  NotificationFilterState,
  NotificationItem,
  NotificationStatusTab,
} from "../types";
import NotificationCard from "./NotificationCard";
import NotificationFilters from "./NotificationFilters";
import NotificationsActions from "./NotificationsActions";
import { useContentStyles } from "./styles";

interface INotificationsContentProps {
  airport: string;
  notifications: NotificationItem[];
  refreshNotifications?: () => Promise<unknown>;
}

const NotificationsContent: React.FC<INotificationsContentProps> = ({
  airport,
  notifications: initialNotifications,
  refreshNotifications,
}) => {
  const styles = useContentStyles();
  const { removeNotifications } = useNotificationWebSocket();
  const [notifications, setNotifications] =
    useState<NotificationItem[]>(initialNotifications);

  const [selectedTab, setSelectedTab] =
    useState<NotificationStatusTab>("active");
  const [filters, setFilters] =
    useState<NotificationFilterState>(DEFAULT_FILTERS);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    setNotifications(initialNotifications);
  }, [initialNotifications]);

  const { timezone, currentTime } = useContext(LayoutContext);
  const { timeZoneId: airportTimeZone } = getAirportConfig(airport);

  const dismissSelectedNotificationsUrl =
    `${process.env.REACT_APP_API_NOTIFICATION}`
      .replace(AIRPORT_CODE_PARAMETER, airport)
      .concat("/dismiss-selected");

  const clearAllNotificationsUrl = `${process.env.REACT_APP_API_NOTIFICATION}`
    .replace(AIRPORT_CODE_PARAMETER, airport)
    .concat("/dismiss");

  const { updateAsync: dismissSelectedNotifications } = useApiResource<
    unknown,
    DismissSelectedNotificationsPayload
  >({
    queryKey: ["dismiss-selected-notifications", airport],
    url: dismissSelectedNotificationsUrl,
    method: "PUT",
    enabled: false,
  });

  const { updateAsync: clearAllNotifications } = useApiResource<
    unknown,
    DismissNotificationsPayload
  >({
    queryKey: ["clear-all-notifications", airport],
    url: clearAllNotificationsUrl,
    method: "PUT",
    enabled: false,
  });

  const hasSelection = selectedIds.size > 0;

  const isLocalTime = timezone === "LT";
  const updatedCurrentTime = isLocalTime
    ? currentTime
    : toZonedTime(fromZonedTime(currentTime, "UTC"), airportTimeZone);

  const clearAllPayload = buildDismissNotificationsPayload(
    filters,
    updatedCurrentTime,
    selectedTab
  );
  const dismissSelectedPayload: DismissSelectedNotificationsPayload = {
    ids: Array.from(selectedIds),
  };

  const notificationsMatchingFilters = notifications.filter((n) => {
    if (
      filters.severity !== "ALL" &&
      n.severity.toUpperCase() !== filters.severity
    ) {
      return false;
    }
    if (
      filters.category !== "All" &&
      n.category.toLowerCase() !== filters.category.toLowerCase()
    ) {
      return false;
    }

    return matchesSelectedTimeFilter(
      n.timestamp,
      filters.time,
      updatedCurrentTime
    );
  });

  const activeNotificationsCount = notificationsMatchingFilters.filter(
    (notification) => !notification.invalid
  ).length;
  const inactiveNotificationsCount =
    notificationsMatchingFilters.length - activeNotificationsCount;

  const filteredNotifications = filterAndSortNotificationsByStatus(
    notificationsMatchingFilters,
    selectedTab
  );

  const formattedNotifications = formatFilteredNotifications(
    filteredNotifications,
    isLocalTime,
    airportTimeZone,
    currentTime
  );

  const handleTabSelect = (value: NotificationStatusTab) => {
    setSelectedTab(value);
    setSelectedIds(new Set());
  };

  const handleFiltersChange = (next: NotificationFilterState) => {
    setFilters(next);
    setSelectedIds(new Set());
  };

  const handleResetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setSelectedIds(new Set());
  };

  const handleToggleSelect = useCallback((id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  const handleDismissSelected = useCallback(async () => {
    const idsToDismiss = new Set(dismissSelectedPayload.ids);

    try {
      await dismissSelectedNotifications(dismissSelectedPayload);
      removeNotifications?.(Array.from(idsToDismiss));
      setNotifications((prev) => prev.filter((n) => !idsToDismiss.has(n.id)));
      setSelectedIds(new Set());
      void refreshNotifications?.();
    } catch {
      // Keep notifications unchanged when the dismiss request fails.
    }
  }, [
    dismissSelectedNotifications,
    dismissSelectedPayload,
    refreshNotifications,
    removeNotifications,
  ]);

  const handleClearAll = useCallback(async () => {
    const filteredIds = new Set(filteredNotifications.map((n) => n.id));

    try {
      await clearAllNotifications(clearAllPayload);
      removeNotifications?.(Array.from(filteredIds));
      setNotifications((prev) => prev.filter((n) => !filteredIds.has(n.id)));
      setSelectedIds(new Set());
      void refreshNotifications?.();
    } catch {
      // Keep notifications unchanged when the dismiss request fails.
    }
  }, [
    clearAllNotifications,
    clearAllPayload,
    filteredNotifications,
    refreshNotifications,
    removeNotifications,
  ]);

  return (
    <section className={styles.content}>
      <TabList
        selectedValue={selectedTab}
        onTabSelect={(_, d) =>
          handleTabSelect(d.value as NotificationStatusTab)
        }
      >
        <Tab value="active" style={{ width: "120px" }}>
          Active ({activeNotificationsCount})
        </Tab>
        <Tab value="inactive" style={{ width: "120px" }}>
          Inactive ({inactiveNotificationsCount})
        </Tab>
      </TabList>
      <Divider className={styles.divider} />

      <NotificationFilters
        filters={filters}
        onChange={handleFiltersChange}
        onReset={handleResetFilters}
      />
      <Divider className={styles.divider} />

      <div className={styles.listHeader}>
        <NotificationsActions
          hasSelection={hasSelection}
          hasNotifications={filteredNotifications.length > 0}
          selectedCount={selectedIds.size}
          allNotificationsCount={filteredNotifications.length}
          actionsClassName={styles.listActions}
          actionLabelClassName={styles.actionLabel}
          onConfirmDismissSelected={handleDismissSelected}
          onConfirmClearAll={handleClearAll}
        />
      </div>

      <div className={styles.list} data-testid="notifications-list">
        {formattedNotifications.length === 0 ? (
          <NoDataCard
            message={EMPTY_STATE_MESSAGE.Notifications}
            marginAll={true}
          />
        ) : (
          formattedNotifications.map((n) => (
            <NotificationCard
              key={n.id}
              notification={n}
              selected={selectedIds.has(n.id)}
              onToggleSelect={handleToggleSelect}
            />
          ))
        )}
      </div>
    </section>
  );
};

export default NotificationsContent;
